import { zodResolver } from '@hookform/resolvers/zod';
import { CheckIcon, ChevronsUpDownIcon, Loader2Icon, PlusIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import type { AdminCreateUserDTO } from '@shared/schemas/auth';
import { adminCreateUserSchema } from '@shared/schemas/auth';
import {
  createCompany,
  createUserAsAdmin,
  getBrowserUrl,
  listCompanies,
  sendActivationEmailAsAdmin,
  type AdminCompanyRecord,
} from '@/lib/auth-client';
import { getAuthErrorMessage } from '@/lib/auth-errors';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';

/**
 * Choix de rôle du formulaire, mappé sur le couple (role plateforme,
 * organizationRole) du contrat : user = membre, company-admin = admin de
 * l'entreprise de rattachement, admin = super admin plateforme.
 */
type RoleChoice = 'user' | 'company-admin' | 'admin';

const roleOptions: Array<{ description: string; label: string; value: RoleChoice }> = [
  {
    description: 'Compte standard avec accès limité aux parcours métier.',
    label: 'Utilisateur',
    value: 'user',
  },
  {
    description: 'Administre les membres, les sources documentaires et l’assistant de son entreprise.',
    label: 'Administrateur de l’entreprise',
    value: 'company-admin',
  },
  {
    description: 'Accès complet à l’application : toutes les entreprises, les utilisateurs et la configuration technique.',
    label: 'Super admin',
    value: 'admin',
  },
];

const accountTypeOptions: Array<{ description: string; label: string; value: AdminCreateUserDTO['accountType'] }> = [
  {
    description: 'Accès limité dans le temps, désactivé automatiquement à expiration.',
    label: 'Démo',
    value: 'demo',
  },
  {
    description: 'Accès standard, sans limite de durée.',
    label: 'Abonné',
    value: 'subscriber',
  },
];

const DEMO_EXPIRATION_PRESETS = [
  { days: 7, label: '1 semaine', value: '1w' },
  { days: 30, label: '1 mois', value: '1m' },
  { days: 90, label: '3 mois', value: '3m' },
] as const;

type DemoExpirationPreset = (typeof DEMO_EXPIRATION_PRESETS)[number]['value'];

/** Préréglage appliqué par défaut : les comptes créés sont des démos d'une semaine. */
const DEFAULT_DEMO_PRESET = DEMO_EXPIRATION_PRESETS[0];

function computeDemoExpirationIso(days: number) {
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
}

function formatDemoExpirationDate(iso: string | undefined) {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
      .format(new Date(iso));
  } catch {
    return null;
  }
}

type CreateUserFormValues = z.input<typeof adminCreateUserSchema>;
type CreateUserFormOutput = z.output<typeof adminCreateUserSchema>;

type CreateUserFormProps = React.ComponentProps<'form'> & {
  showIntro?: boolean;
  surface?: 'embedded' | 'standalone';
};

export function CreateUserForm({
  className,
  showIntro = true,
  surface = 'standalone',
  ...props
}: CreateUserFormProps) {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<AdminCompanyRecord[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);
  const [isCompanyComboboxOpen, setIsCompanyComboboxOpen] = useState(false);
  const [isSuperAdminConfirmOpen, setIsSuperAdminConfirmOpen] = useState(false);
  const [isCreateCompanyOpen, setIsCreateCompanyOpen] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [isCreatingCompany, setIsCreatingCompany] = useState(false);
  const [demoExpirationPreset, setDemoExpirationPreset] = useState<DemoExpirationPreset>(
    DEFAULT_DEMO_PRESET.value,
  );
  const form = useForm<CreateUserFormValues, undefined, CreateUserFormOutput>({
    defaultValues: {
      accountType: 'demo',
      companyId: '',
      demoExpiresAt: computeDemoExpirationIso(DEFAULT_DEMO_PRESET.days),
      email: '',
      name: '',
      role: 'user',
    },
    resolver: zodResolver(adminCreateUserSchema),
  });
  const selectedRole = useWatch({
    control: form.control,
    name: 'role',
  });
  const selectedOrganizationRole = useWatch({
    control: form.control,
    name: 'organizationRole',
  });
  const selectedRoleChoice: RoleChoice = selectedRole === 'admin'
    ? 'admin'
    : selectedOrganizationRole === 'admin'
      ? 'company-admin'
      : 'user';
  const selectedAccountType = useWatch({
    control: form.control,
    name: 'accountType',
  });
  const selectedDemoExpiresAt = useWatch({
    control: form.control,
    name: 'demoExpiresAt',
  });
  const selectedCompanyId = useWatch({
    control: form.control,
    name: 'companyId',
  });
  const selectedCompany = companies.find((company) => company.id === selectedCompanyId) ?? null;
  // Le rôle super admin n'est proposé que pour les comptes abonnés.
  const visibleRoleOptions = selectedAccountType === 'demo'
    ? roleOptions.filter((roleOption) => roleOption.value !== 'admin')
    : roleOptions;

  async function submitCreateCompany() {
    const companyName = newCompanyName.trim();

    if (companyName.length < 2) {
      toast.error('Le nom de l’entreprise doit contenir au moins 2 caractères.');
      return;
    }

    setIsCreatingCompany(true);
    const result = await createCompany({ name: companyName });
    setIsCreatingCompany(false);

    if (result.error || !result.data?.data) {
      toast.error(
        result.error
          ? getAuthErrorMessage(result.error)
          : 'La création de l’entreprise a échoué.',
      );
      return;
    }

    const createdCompany = result.data.data;
    setCompanies((previousCompanies) => [...previousCompanies, createdCompany]
      .sort((first, second) => first.name.localeCompare(second.name, 'fr')));
    // La nouvelle entreprise devient le rattachement du compte en cours de création.
    form.setValue('companyId', createdCompany.id, { shouldDirty: true, shouldValidate: true });
    // Lève l'erreur « aucune entreprise disponible » posée au chargement.
    form.clearErrors('root');
    setNewCompanyName('');
    setIsCreateCompanyOpen(false);
    toast.success(`Entreprise ${createdCompany.name} créée.`);
  }

  function applyRoleChoice(choice: Exclude<RoleChoice, 'admin'>) {
    form.setValue('role', 'user', { shouldDirty: true, shouldValidate: true });
    form.setValue('organizationRole', choice === 'company-admin' ? 'admin' : undefined, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }

  useEffect(() => {
    let isCancelled = false;

    async function loadCompanies() {
      const result = await listCompanies();
      if (isCancelled) {
        return;
      }

      if (result.error || !result.data?.data?.length) {
        const message = result.error
          ? getAuthErrorMessage(result.error)
          : 'Aucune entreprise disponible pour rattacher ce compte.';
        form.setError('root', { message });
        setIsLoadingCompanies(false);
        return;
      }

      const nextCompanies = result.data.data;
      setCompanies(nextCompanies);
      form.setValue('companyId', nextCompanies[0]?.id ?? '', {
        shouldDirty: false,
        shouldValidate: true,
      });
      setIsLoadingCompanies(false);
    }

    void loadCompanies();

    return () => {
      isCancelled = true;
    };
  }, [form]);

  async function handleSubmit(values: CreateUserFormOutput) {
    form.clearErrors('root');

    const result = await createUserAsAdmin(values);

    if (result.error) {
      const message = getAuthErrorMessage(result.error);
      form.setError('root', { message });
      toast.error(message);
      return;
    }

    const verificationResult = await sendActivationEmailAsAdmin({
      callbackURL: getBrowserUrl(`/auth/set-password?email=${encodeURIComponent(values.email)}`),
      email: values.email,
    });

    if (verificationResult.error) {
      const message = getAuthErrorMessage(verificationResult.error);
      form.setError('root', { message });
      toast.error(message);
      return;
    }

    toast.success(`Compte ${values.email} créé et email d activation envoyé.`);
    navigate('/admin/users', { replace: true });
  }

  return (
    <form
      className={cn(
        surface === 'embedded'
          ? 'bg-transparent p-0'
          : 'rounded-[var(--radius-surface-4xl)] border border-border bg-card p-6 shadow-[0_8px_24px_rgba(15,23,42,0.03)]',
        className,
      )}
      onSubmit={form.handleSubmit(handleSubmit)}
      {...props}
    >
      <FieldGroup>
        {showIntro ? (
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold text-foreground">Créer un compte administré</h2>
            <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
              L’utilisateur recevra un e-mail pour activer son accès.
            </p>
          </div>
        ) : null}

        <div className="grid gap-5 lg:grid-cols-2">
          <Field>
            <FieldLabel htmlFor="create-user-name">Nom complet</FieldLabel>
            <Input
              id="create-user-name"
              data-testid="create-user-name"
              type="text"
              autoComplete="name"
              placeholder="Anne Onyme"
              aria-invalid={form.formState.errors.name ? 'true' : 'false'}
              {...form.register('name')}
            />
            <FieldError errors={[form.formState.errors.name]} />
          </Field>

          <Field>
            <FieldLabel htmlFor="create-user-company">Entreprise</FieldLabel>
            <div className="flex items-center gap-2">
              <Popover open={isCompanyComboboxOpen} onOpenChange={setIsCompanyComboboxOpen}>
                <PopoverTrigger asChild>
                  <Button
                    id="create-user-company"
                    type="button"
                    variant="outline"
                    role="combobox"
                    data-testid="create-user-company"
                    aria-expanded={isCompanyComboboxOpen}
                    disabled={isLoadingCompanies}
                    className="h-10 min-w-0 flex-1 justify-between border-input bg-background px-3 font-normal hover:bg-background"
                  >
                    <span className="truncate">
                      {isLoadingCompanies
                        ? 'Chargement des entreprises...'
                        : (selectedCompany?.name ?? 'Sélectionner une entreprise')}
                    </span>
                    <ChevronsUpDownIcon className="size-4 shrink-0 opacity-60" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Rechercher une entreprise..." />
                    <CommandList>
                      <CommandEmpty>Aucune entreprise trouvée.</CommandEmpty>
                      <CommandGroup>
                        {companies.map((company) => (
                          <CommandItem
                            key={company.id}
                            value={company.name}
                            onSelect={() => {
                              form.setValue('companyId', company.id, {
                                shouldDirty: true,
                                shouldValidate: true,
                              });
                              setIsCompanyComboboxOpen(false);
                            }}
                          >
                            <CheckIcon
                              className={cn(
                                'mr-2 size-4',
                                selectedCompanyId === company.id ? 'opacity-100' : 'opacity-0',
                              )}
                            />
                            {company.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <Button
                type="button"
                variant="outline"
                data-testid="create-user-create-company"
                onClick={() => setIsCreateCompanyOpen(true)}
                title="Créer une entreprise"
                className="h-10 shrink-0 border-input bg-background px-3 font-normal hover:bg-muted"
              >
                <PlusIcon className="size-4" />
                Entreprise
              </Button>
            </div>
            <FieldError errors={[form.formState.errors.companyId]} />
          </Field>

          <Field>
            <FieldLabel htmlFor="create-user-email">Email</FieldLabel>
            <Input
              id="create-user-email"
              data-testid="create-user-email"
              type="email"
              autoComplete="email"
              placeholder="anne.onyme@example.com"
              aria-invalid={form.formState.errors.email ? 'true' : 'false'}
              {...form.register('email')}
            />
            <FieldError errors={[form.formState.errors.email]} />
          </Field>
        </div>

        <Field>
          <FieldLabel>Type de compte</FieldLabel>
          <RadioGroup
            value={selectedAccountType}
            onValueChange={(value) => {
              const nextAccountType = value as AdminCreateUserDTO['accountType'];
              form.setValue('accountType', nextAccountType, {
                shouldDirty: true,
                shouldValidate: true,
              });
              if (nextAccountType === 'demo') {
                // Le rôle super admin est réservé aux comptes abonnés.
                if (form.getValues('role') === 'admin') {
                  form.setValue('role', 'user', { shouldDirty: true, shouldValidate: true });
                }
                setDemoExpirationPreset(DEFAULT_DEMO_PRESET.value);
                form.setValue('demoExpiresAt', computeDemoExpirationIso(DEFAULT_DEMO_PRESET.days), {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              } else {
                form.setValue('demoExpiresAt', undefined, { shouldDirty: true, shouldValidate: true });
              }
            }}
            className="grid gap-3 md:grid-cols-2"
          >
            {accountTypeOptions.map((option) => (
              <label
                key={option.value}
                data-testid={`create-user-account-type-${option.value}`}
                className={cn(
                  'flex cursor-pointer items-start gap-3 rounded-[var(--radius-surface-3xl)] border bg-background p-4 text-left transition-colors',
                  selectedAccountType === option.value
                    ? 'border-primary ring-1 ring-primary'
                    : 'border-border hover:border-foreground/20 hover:bg-muted/30',
                )}
              >
                <RadioGroupItem value={option.value} id={`create-user-account-type-${option.value}`} className="mt-0.5" />
                <div className="space-y-1">
                  <p className="font-medium text-foreground">{option.label}</p>
                  <p className="text-sm leading-6 text-muted-foreground">{option.description}</p>
                </div>
              </label>
            ))}
          </RadioGroup>
          <FieldError errors={[form.formState.errors.accountType]} />
        </Field>

        {selectedAccountType === 'demo' ? (
          <div className="rounded-[var(--radius-surface-3xl)] border border-border bg-muted/40 p-5">
            <div className="mb-4 space-y-1">
              <h3 className="text-base font-semibold text-foreground">Durée du compte démo</h3>
              <p className="text-sm text-muted-foreground">
                Le compte sera désactivé automatiquement à la date d’expiration. Il pourra ensuite être
                réactivé depuis la page utilisateurs.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Durée du compte démo">
              {DEMO_EXPIRATION_PRESETS.map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  data-testid={`create-user-demo-expiration-${preset.value}`}
                  onClick={() => {
                    setDemoExpirationPreset(preset.value);
                    form.setValue('demoExpiresAt', computeDemoExpirationIso(preset.days), {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                  className={cn(
                    'rounded-full border px-4 py-1.5 text-sm font-medium transition-colors',
                    demoExpirationPreset === preset.value
                      ? 'border-primary bg-primary/10 text-foreground ring-1 ring-primary'
                      : 'border-border bg-background text-muted-foreground hover:border-foreground/20 hover:bg-muted/40',
                  )}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            {formatDemoExpirationDate(selectedDemoExpiresAt) ? (
              <p className="mt-3 text-sm text-muted-foreground">
                Expire le <span className="font-medium text-foreground">{formatDemoExpirationDate(selectedDemoExpiresAt)}</span>
              </p>
            ) : null}
            <FieldError errors={[form.formState.errors.demoExpiresAt]} />
          </div>
        ) : null}

        <Field>
          <FieldLabel>Rôle initial</FieldLabel>
          <RadioGroup
            value={selectedRoleChoice}
            onValueChange={(value) => {
              const nextChoice = value as RoleChoice;
              // L'attribution du rôle super admin passe par une confirmation explicite.
              if (nextChoice === 'admin') {
                setIsSuperAdminConfirmOpen(true);
                return;
              }
              applyRoleChoice(nextChoice);
            }}
            className={cn(
              'grid gap-3',
              visibleRoleOptions.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2',
            )}
          >
            {visibleRoleOptions.map((roleOption) => (
              <label
                key={roleOption.value}
                data-testid={`create-user-role-${roleOption.value}`}
                className={cn(
                  'flex cursor-pointer items-start gap-3 rounded-[var(--radius-surface-3xl)] border bg-background p-4 text-left transition-colors',
                  selectedRoleChoice === roleOption.value
                    ? 'border-primary ring-1 ring-primary'
                    : 'border-border hover:border-foreground/20 hover:bg-muted/30',
                )}
              >
                <RadioGroupItem value={roleOption.value} id={`create-user-role-${roleOption.value}`} className="mt-0.5" />
                <div className="space-y-1">
                  <p className="font-medium text-foreground">{roleOption.label}</p>
                  <p className="text-sm leading-6 text-muted-foreground">
                    {roleOption.description}
                  </p>
                </div>
              </label>
            ))}
          </RadioGroup>
          <FieldError errors={[form.formState.errors.role]} />
        </Field>

        <Dialog
          open={isCreateCompanyOpen}
          onOpenChange={(open) => {
            if (isCreatingCompany) return;
            setIsCreateCompanyOpen(open);
            if (!open) setNewCompanyName('');
          }}
        >
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Créer une entreprise</DialogTitle>
              <DialogDescription>
                Elle sera immédiatement disponible pour rattacher ce compte.
              </DialogDescription>
            </DialogHeader>
            <Field>
              <FieldLabel htmlFor="create-user-company-name">Nom de l’entreprise</FieldLabel>
              <Input
                id="create-user-company-name"
                data-testid="create-user-company-name"
                disabled={isCreatingCompany}
                onChange={(event) => setNewCompanyName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void submitCreateCompany();
                  }
                }}
                placeholder="Ex: Inersio France"
                value={newCompanyName}
              />
            </Field>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={isCreatingCompany}
                onClick={() => setIsCreateCompanyOpen(false)}
              >
                Annuler
              </Button>
              <Button
                type="button"
                data-testid="create-user-company-submit"
                disabled={isCreatingCompany}
                onClick={() => void submitCreateCompany()}
              >
                {isCreatingCompany ? <Loader2Icon className="animate-spin" /> : null}
                Créer l’entreprise
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AlertDialog open={isSuperAdminConfirmOpen} onOpenChange={setIsSuperAdminConfirmOpen}>
          <AlertDialogContent className="max-w-md rounded-3xl">
            <AlertDialogHeader>
              <AlertDialogTitle>Attribuer le rôle super admin ?</AlertDialogTitle>
              <AlertDialogDescription>
                Ce rôle donne accès à tous les droits de l’application : gestion de toutes les entreprises et de tous
                les utilisateurs, configuration technique de la plateforme. À réserver aux comptes de confiance.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="create-user-super-admin-cancel">Annuler</AlertDialogCancel>
              <AlertDialogAction
                data-testid="create-user-super-admin-confirm"
                onClick={() => {
                  form.setValue('role', 'admin', { shouldDirty: true, shouldValidate: true });
                  form.setValue('organizationRole', undefined, { shouldDirty: true, shouldValidate: true });
                }}
              >
                Confirmer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <FieldError errors={[form.formState.errors.root]} />

        <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
          <Button
            asChild
            type="button"
            variant="outline"
            className="h-12 border-border bg-background px-5 text-[15px] font-medium text-foreground hover:border-foreground/20 hover:bg-muted dark:bg-muted dark:hover:bg-muted/80"
          >
            <Link to="/admin/users">Annuler</Link>
          </Button>
          <Button
            data-testid="create-user-submit"
            type="submit"
            disabled={form.formState.isSubmitting}
            className="h-12 border-black bg-black px-6 text-[15px] font-medium text-white shadow-none transition-colors hover:border-black/85 hover:bg-black/85 hover:text-white dark:border-border dark:bg-muted dark:text-foreground dark:hover:border-foreground/20 dark:hover:bg-muted/80 dark:hover:text-foreground"
          >
            {form.formState.isSubmitting ? <Loader2Icon className="animate-spin" /> : null}
            Créer l’utilisateur
          </Button>
        </div>
      </FieldGroup>
    </form>
  );
}
